import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SystemHeader from './SystemHeader';
import { CungVeDichAPI } from '../services/API';

const InventoryForm = ({ userInfo, onLogout, token, totalPoints, accessToken, onRefreshPoints, onClose }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [lastUpdatedDate, setLastUpdatedDate] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  // Helper functions for price formatting
  const formatPrice = (value) => {
    if (!value || value === '' || isNaN(parseFloat(value))) return '';
    return parseFloat(value).toLocaleString('vi-VN');
  };

  const parsePrice = (formattedValue) => {
    if (!formattedValue || formattedValue === '') return '';
    // Remove dots (thousand separators) and replace comma with dot for decimal
    return formattedValue.replace(/\./g, '').replace(',', '.');
  };

  // Load products from API
  useEffect(() => {
    const loadProducts = async () => {
      if (!accessToken) {
        console.warn('No access token available for loading products');
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        console.log('Loading product inventory from API...');

        const result = await CungVeDichAPI.getProductInventory(accessToken);

        if (result.success && result.products) {
          // Get the latest updated date if available
          if (result.products.length > 0) {
            const productsWithUpdatedAt = result.products.filter(product => product.updatedAt);
            if (productsWithUpdatedAt.length > 0) {
              const latestUpdate = productsWithUpdatedAt.reduce((latest, product) => {
                const productDate = new Date(product.updatedAt);
                const latestDate = new Date(latest);
                return productDate > latestDate ? product.updatedAt : latest;
              }, productsWithUpdatedAt[0].updatedAt);

              setLastUpdatedDate(latestUpdate);
            }
          }

          // Transform API data to form format
          const transformedProducts = result.products.map(product => ({
            ...product,
            newStock: product.quantity?.toString() || '',
            oldStock: product.extQuantity?.toString() || ''
          }));

          // Sort products by name in ascending order
          const sortedProducts = transformedProducts.sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });

          setProducts(sortedProducts);
          console.log('Products loaded and sorted successfully:', sortedProducts.length);
        } else {
          console.error('Failed to load products:', result);
          setMessage('Không thể tải danh sách sản phẩm từ server');
          setMessageType('error');
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setMessage('Có lỗi xảy ra khi tải danh sách sản phẩm: ' + error.message);
        setMessageType('error');
      } finally {
        setInitialLoading(false);
      }
    };

    loadProducts();
  }, [accessToken]);

  const handleStockChange = (productId, field, value) => {
    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, [field]: value }
          : product
      )
    );
  };

  const handleStockBlur = (productId, field, value) => {
    // Convert invalid values to 0 when user leaves the input
    let normalizedValue = value;

    if (value === '' || value === null || value === undefined || isNaN(parseInt(value)) || parseInt(value) < 0) {
      normalizedValue = '0';
    } else {
      // Ensure it's a valid non-negative integer
      normalizedValue = Math.max(0, parseInt(value)).toString();
    }

    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, [field]: normalizedValue }
          : product
      )
    );
  };

  const handlePriceChange = (productId, formattedValue) => {
    // Parse the formatted value to get raw number
    const rawValue = parsePrice(formattedValue);

    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, price: rawValue }
          : product
      )
    );
  };

  const handlePriceBlur = (productId, value) => {
    // Convert invalid values to 0 when user leaves the input
    let normalizedValue = value;

    if (value === '' || value === null || value === undefined || isNaN(parseFloat(value)) || parseFloat(value) < 0) {
      normalizedValue = '0';
    } else {
      // Ensure it's a valid non-negative number
      normalizedValue = Math.max(0, parseFloat(value)).toString();
    }

    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === productId
          ? { ...product, price: normalizedValue }
          : product
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    // Normalize all values before validation (convert empty/invalid values to 0)
    setProducts(prevProducts =>
      prevProducts.map(product => ({
        ...product,
        newStock: product.newStock === '' || product.newStock === null || product.newStock === undefined || isNaN(parseInt(product.newStock)) || parseInt(product.newStock) < 0
          ? '0'
          : Math.max(0, parseInt(product.newStock)).toString(),
        oldStock: product.oldStock === '' || product.oldStock === null || product.oldStock === undefined || isNaN(parseInt(product.oldStock)) || parseInt(product.oldStock) < 0
          ? '0'
          : Math.max(0, parseInt(product.oldStock)).toString()
      }))
    );

    // No validation needed since we normalize all values to valid numbers

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);
    setLoading(true);
    setMessage('');

    try {
      // Prepare data for submission
      const inventoryData = products.map(product => ({
        productId: product.id,
        quantity: parseInt(product.newStock) || 0,
        extQuantity: parseInt(product.oldStock) || 0,
        price: parseFloat(product.price) || 0
      }));

      console.log('Inventory data to submit:', inventoryData);

      // Call API to update inventory
      const result = await CungVeDichAPI.submitInventoryUpdate(inventoryData, accessToken);

      if (result.success) {
        setMessage('Cập nhật kho hàng thành công!');
        setMessageType('success');

        // If onClose callback provided, use it; otherwise navigate
        setTimeout(() => {
          if (onClose) {
            onClose();
          } else {
            navigate('/kho-hang');
          }
        }, 1500); // Wait 1.5 seconds to show success message
      } else {
        throw new Error(result.message || 'Cập nhật thất bại');
      }

    } catch (error) {
      console.error('Error submitting inventory:', error);
      setMessage('Có lỗi xảy ra khi cập nhật kho hàng. Vui lòng thử lại.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // const handleImageClick = (imageUrl, productName, productDescription) => {
  //   setSelectedImage({ url: imageUrl, name: productName, description: productDescription });
  //   setShowImageModal(true);
  // };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };


  const tongSl = products.reduce((t, p) => t + (parseInt(p.newStock) || 0) + (parseInt(p.oldStock) || 0), 0);
  const tongTien = products.reduce((t, p) => t + ((parseInt(p.newStock) || 0) + (parseInt(p.oldStock) || 0)) * (parseFloat(p.price) || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <SystemHeader
        userInfo={userInfo}
        onLogout={onLogout}
        totalPoints={totalPoints}
        pageTitle="Chỉnh sửa kho hàng"
        pageSubtitle="Nhập/Chỉnh sửa thông tin kho hàng"
        onRefreshPoints={onRefreshPoints}
      />
      {/* Close button when opened from OrderForm */}
      {onClose && (
        <div className="max-w-4xl mx-auto px-4 sm:px-4 lg:px-6 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Quay lại đơn hàng
          </button>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-1 sm:px-4 lg:px-6">
        <div className="bg-white rounded-lg shadow-sm border">

          <form onSubmit={handleSubmit}>
            {/* Message Display */}
            {message && (
              <div className={`mx-6 mt-6 p-4 rounded-lg flex-shrink-0 ${messageType === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                {message}
              </div>
            )}

            {/* Products List - Compact Table */}
            <div>
              {initialLoading ? (
                <div className="flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Đang tải danh sách sản phẩm...</span>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center">
                  <p className="text-gray-500">Không có sản phẩm nào để hiển thị</p>
                </div>
              ) : (
                <div>
                  <table className="w-full border-collapse">
                    <thead className="sticky top-0 z-20 shadow" style={{ position: 'sticky', top: 0 }}>
                      <tr className="bg-blue-50">
                        <th colSpan={4} className="px-2 py-1.5 text-left text-sm font-normal text-gray-700">
                          Tổng SL: <b className="text-gray-900">{tongSl.toLocaleString('vi-VN')}</b>
                          <span className="mx-2 text-gray-300">|</span>
                          Tiền: <b className="text-orange-600">{tongTien.toLocaleString('vi-VN')}₫</b>
                        </th>
                      </tr>
                      <tr className="bg-gray-100 text-xs font-semibold text-gray-700">
                        <th className="px-2 py-1 text-left border-b border-gray-300">Sản phẩm</th>
                        <th className="px-1 py-1 text-center border-b border-gray-300">SL</th>
                        <th className="px-1 py-1 text-center border-b border-gray-300">Đơn giá</th>
                        <th className="px-1 py-1 text-right border-b border-gray-300">Thành tiền</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product, index) => {
                        const newStock = parseInt(product.newStock) || 0;
                        const oldStock = parseInt(product.oldStock) || 0;
                        const total = newStock + oldStock;

                        return (
                          <tr key={product.id} className="hover:bg-gray-50">
                            {/* Product Info - Compact */}
                            <td className="px-2 py-1">
                              <div className="flex items-center space-x-2">
                                {/* {product.imageUrl && (
                                  <div
                                    className="w-8 h-8 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                    onClick={() => handleImageClick(product.imageUrl, product.name, product.description)}
                                  >
                                    <img
                                      src={product.imageUrl}
                                      alt={product.name}
                                      className="w-full h-full object-cover rounded border border-gray-200"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )} */}
                                <div className="text-sm font-medium text-gray-900 leading-tight flex-1" title={product.name}>
                                  <div
                                    className="overflow-hidden"
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      maxHeight: '2.5em'
                                    }}
                                  >
                                    {product.name}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Old Stock Input */}
                            {/* <td className="px-1 py-2">
                              <input
                                type="number"
                                min="0"
                                value={product.oldStock}
                                style={{ width: '45px', minHeight: '30px' }}
                                onChange={(e) => handleStockChange(product.id, 'oldStock', e.target.value)}
                                onBlur={(e) => handleStockBlur(product.id, 'oldStock', e.target.value)}
                                className="px-1 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-center"
                                placeholder="0"
                              />
                            </td> */}

                            {/* New Stock Input.
                                Android cũ: nếu dùng type="number" + container fixed-height
                                + font-size < 16px → bàn phím che input, value "biến mất".
                                Fix: type=text + inputMode=numeric (vẫn ra bàn phím số),
                                font 16px (chống zoom iOS), scrollIntoView khi focus. */}
                            <td className="px-0.5 py-1">
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={product.newStock}
                                style={{ width: '56px', height: '40px', fontSize: '16px' }}
                                onChange={(e) => handleStockChange(product.id, 'newStock', e.target.value.replace(/[^0-9]/g, ''))}
                                onBlur={(e) => handleStockBlur(product.id, 'newStock', e.target.value)}
                                onFocus={(e) => {
                                  const el = e.target;
                                  setTimeout(() => {
                                    try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
                                  }, 250);
                                }}
                                className="px-1 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-center"
                                placeholder="0"
                              />
                            </td>

                            {/* Product Price Input — same Android keyboard fix */}
                            <td className="px-0.5 py-1">
                              <input
                                type="text"
                                inputMode="numeric"
                                value={formatPrice(product.price)}
                                style={{ width: '88px', height: '40px', fontSize: '16px' }}
                                onChange={(e) => handlePriceChange(product.id, e.target.value)}
                                onBlur={(e) => handlePriceBlur(product.id, parsePrice(e.target.value))}
                                onFocus={(e) => {
                                  const el = e.target;
                                  setTimeout(() => {
                                    try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
                                  }, 250);
                                }}
                                className="px-1 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent text-center"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-1 py-1 text-right text-xs font-semibold text-blue-700 whitespace-nowrap">
                              {(total * (product.price || 0)).toLocaleString('vi-VN')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="sticky bottom-0 border-t border-gray-200 bg-white py-4 z-10">
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Đang xử lý...
                    </div>
                  ) : (
                    'Lưu kho hàng'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-6">
          <div className="max-w-xl mx-auto bg-white rounded-xl shadow-lg p-4 w-full max-h-[90vh] overflow-y-auto">
            <h1 className="text-lg font-bold text-center mb-4 text-gray-800">
              {userInfo?.id || 'N/A'}-{userInfo?.name || 'N/A'} ( Kho Hàng)
            </h1>

            {/* Bảng báo cáo chi tiết */}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="pb-2 font-semibold text-left text-gray-600">Sản phẩm</th>
                  {/* <th className="pb-2 px-1 font-semibold text-center text-gray-600">Cty cũ</th> */}
                  <th className="pb-2 px-1 font-semibold text-center text-gray-600">SL</th>
                  <th className="pb-2 px-1 font-semibold text-center text-gray-600">Đơn giá</th>
                  <th className="pb-2 font-semibold text-right text-gray-600">TT</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .filter(product => {
                    const oldStock = parseInt(product.oldStock) || 0;
                    const newStock = parseInt(product.newStock) || 0;
                    return (oldStock + newStock) > 0;
                  })
                  .map((product, index) => {
                    const oldStock = parseInt(product.oldStock) || 0;
                    const newStock = parseInt(product.newStock) || 0;
                    const total = oldStock + newStock;

                    return (
                      <tr key={product.id} className="border-b border-gray-200 last:border-b-0">
                        <td className="py-1.5 pr-1 align-top">
                          <div className="flex items-center space-x-2">
                            {/* {product.imageUrl && (
                              <div
                                className="w-8 h-8 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => handleImageClick(product.imageUrl, product.name, product.description)}
                              >
                                <img
                                  src={product.imageUrl}
                                  alt={product.name}
                                  className="w-full h-full object-cover rounded border border-gray-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </div>
                            )} */}
                            <span className="text-sm">{product.name}</span>
                          </div>
                        </td>
                        {/* <td className="py-1.5 px-1 text-center align-top text-gray-600">
                          {oldStock.toLocaleString()}
                        </td> */}
                        <td className="py-1.5 px-1 text-center align-top text-gray-600">
                          {newStock.toLocaleString()}
                        </td>
                        <td className="py-1.5 px-1 text-center align-top text-gray-600">
                          {formatPrice(product.price)}₫
                        </td>
                        <td className="py-1.5 pl-1 text-right font-medium align-top">
                          {(total * (product.price || 0)).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td colSpan="2" className="pt-2 font-bold text-left text-base text-gray-800">Tổng cộng</td>
                  {/* <td className="pt-2 font-bold text-center text-base text-gray-800">
                    {products
                      .reduce((total, product) => total + (parseInt(product.oldStock) || 0), 0)
                      .toLocaleString()}
                  </td> */}
                  <td className="pt-2 font-bold text-center text-base text-gray-800">
                    {products
                      .reduce((total, product) => total + (parseInt(product.newStock) || 0), 0)
                      .toLocaleString()}
                  </td>
                  <td></td>
                  <td className="pt-2 font-bold text-right text-base text-orange-600">
                    {products
                      .reduce((total, product) => {
                        const oldStock = parseInt(product.oldStock) || 0;
                        const newStock = parseInt(product.newStock) || 0;
                        const price = parseFloat(product.price) || 0;
                        return total + ((oldStock + newStock) * price);
                      }, 0)
                      .toLocaleString()}₫
                  </td>
                  {/* <td className="pt-2 font-bold text-right text-base text-gray-800">
                    {products
                      .reduce((total, product) => {
                        const oldStock = parseInt(product.oldStock) || 0;
                        const newStock = parseInt(product.newStock) || 0;
                        return total + oldStock + newStock;
                      }, 0)
                      .toLocaleString()}
                  </td> */}
                </tr>
                {/* <tr className="border-t border-gray-200">
                  <td colSpan="4" className="pt-2 font-bold text-left text-base text-gray-800">Tổng giá trị</td>
                </tr> */}
              </tfoot>
            </table>

            {/* Lưu ý quan trọng - di chuyển xuống dưới */}
            <div className="mt-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <svg className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-xs text-red-700">
                  <span className="font-medium">Lưu ý:</span> Vui lòng kiểm tra kỹ thông tin trước khi xác nhận. Chụp ảnh báo cáo gửi cấp trên để duyệt.
                </div>
              </div>
            </div>

            {/* Ngày cập nhật kho hàng gần nhất */}
            {lastUpdatedDate && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="text-xs text-green-700">
                    <span className="font-medium">Ngày cập nhật kho hàng gần nhất:</span> {new Date(lastUpdatedDate).toLocaleDateString('vi-VN', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowConfirmDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowConfirmDialog(false);
                  await handleConfirmSubmit();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Xác nhận lưu
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">{selectedImage.name}</h3>
                <button
                  onClick={closeImageModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="w-full h-auto max-h-[60vh] object-contain rounded-lg mb-4"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y3ZjdmNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBub3QgZm91bmQ8L3RleHQ+PC9zdmc+';
                }}
              />
              {selectedImage.description && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Mô tả sản phẩm:</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {selectedImage.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryForm;
